import { Fragment, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { PageContainer } from './PageContainer';
import { IconTile } from './ui/icon-tile/IconTile';
import { TextLink } from './ui/text-link/TextLink';

export interface BreadcrumbItem {
    label: string;
    to?: string;
    /** Router location state for `to`, read by the destination (a back reference, for example). */
    state?: unknown;
}

export interface DetailPageHeaderProps {
    /** The last item is the current page and is not linked. */
    breadcrumbs: BreadcrumbItem[];
    title: string;
    icon?: LucideIcon;
    badges?: ReactNode;
    subtitle?: ReactNode;
    actions?: ReactNode;
    children?: ReactNode;
    /** A `TabList` with `variant="header"`, drawn on the band's bottom border. */
    tabs?: ReactNode;
}

function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
    return (
        <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap gap-2 text-sm text-mp-muted">
                {items.map((item, index) => {
                    const isCurrent = index === items.length - 1;
                    return (
                        <Fragment key={`${item.label}-${index}`}>
                            {index > 0 && <li aria-hidden="true">/</li>}
                            <li>
                                {isCurrent || !item.to ? (
                                    <span
                                        aria-current={isCurrent ? 'page' : undefined}
                                        className="text-mp-foreground"
                                    >
                                        {item.label}
                                    </span>
                                ) : (
                                    <TextLink to={item.to} state={item.state} color="secondary" className="text-sm">
                                        {item.label}
                                    </TextLink>
                                )}
                            </li>
                        </Fragment>
                    );
                })}
            </ol>
        </nav>
    );
}

/** Full width only on a route with `FULL_WIDTH_PAGE`; elsewhere it sits inside the page padding. */
export function DetailPageHeader({
    breadcrumbs,
    title,
    icon,
    badges,
    subtitle,
    actions,
    children,
    tabs,
}: DetailPageHeaderProps) {
    return (
        <header className="border-b border-mp-border bg-mp-surface">
            <PageContainer verticalPadding={false}>
                <div className={`space-y-4 pt-5 ${tabs ? '' : 'pb-5'}`}>
                    <Breadcrumbs items={breadcrumbs} />
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            {icon && <IconTile icon={icon} size="lg" />}
                            <div className="space-y-1">
                                <div className="flex flex-wrap items-center gap-3">
                                    <h1 className="text-3xl font-bold text-mp-foreground">
                                        {title}
                                    </h1>
                                    {badges}
                                </div>
                                {subtitle && (
                                    <div className="text-sm text-mp-muted">{subtitle}</div>
                                )}
                            </div>
                        </div>
                        {actions && (
                            <div className="flex flex-wrap items-center gap-2">{actions}</div>
                        )}
                    </div>
                    {children}
                    {tabs}
                </div>
            </PageContainer>
        </header>
    );
}
